import ShareIcon from '@mui/icons-material/Share';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EventIcon from '@mui/icons-material/Event';
import GoogleIcon from '@mui/icons-material/Google';
import PlaceIcon from '@mui/icons-material/Place';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  FormControlLabel,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import { memo, useState, useCallback } from 'react';

import type { Meeting, Group, UserProfile } from '@penticton-dev-events/shared';
import type { RSVP, RSVPStatus, RSVPApprovalStatus } from '../api/rsvpRepository';
import {
  downloadICSFile,
  generateGoogleCalendarURL,
  generateOutlookURL,
} from '../../../utils/ics-export';

export type EventActionsProps = {
  readonly meeting: Meeting;
  readonly group?: Group;
  readonly user?: UserProfile | null;
  readonly rsvp: RSVP | null;
  readonly rsvpStatus: RSVPStatus | null;
  readonly rsvpLoading: boolean;
  readonly attendingCount: number;
  readonly isNotificationSubscribed: boolean;
  readonly notificationLoading: boolean;
  readonly isPushSupported: boolean;
  readonly notificationPermission: NotificationPermission;
  readonly onRSVP: (status: RSVPStatus) => Promise<void>;
  readonly onToggleNotifications: () => Promise<void>;
};

/**
 * EventActions provides RSVP buttons, sharing, calendar export, and notifications.
 * Handles capacity-aware RSVP states and provides comprehensive event interaction options.
 */
const EventActions = memo<EventActionsProps>(
  ({
    meeting,
    group,
    user,
    rsvp,
    rsvpStatus,
    rsvpLoading,
    attendingCount,
    isNotificationSubscribed,
    notificationLoading,
    isPushSupported,
    notificationPermission,
    onRSVP,
    onToggleNotifications,
  }) => {
    const [calendarMenuAnchor, setCalendarMenuAnchor] = useState<HTMLElement | null>(
      null,
    );
    const [shareMenuAnchor, setShareMenuAnchor] = useState<HTMLElement | null>(null);
    const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: 'success' | 'error' | 'info';
    }>({ open: false, message: '', severity: 'info' });

    const spotsRemaining = meeting.capacity ? meeting.capacity - attendingCount : null;
    const isAtCapacity = spotsRemaining !== null && spotsRemaining <= 0;
    const canRSVP = user && meeting.status === 'scheduled';
    const canAttend = canRSVP && (!isAtCapacity || rsvpStatus === 'attending');

    const showSnackbar = useCallback(
      (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
        setSnackbar({ open: true, message, severity });
      },
      [],
    );

    const handleRSVP = useCallback(
      async (status: RSVPStatus) => {
        try {
          await onRSVP(status);
          // Success message based on the action taken
          let message = `Successfully marked as ${status.replace('_', ' ')}`;
          if (status === 'not_attending' && meeting.requiresApproval) {
            message = 'RSVP removed';
          }
          showSnackbar(message, 'success');
        } catch (error) {
          showSnackbar('Failed to update RSVP. Please try again.', 'error');
        }
      },
      [onRSVP, meeting.requiresApproval, showSnackbar],
    );

    const handleToggleNotifications = useCallback(async () => {
      try {
        await onToggleNotifications();
        const action = isNotificationSubscribed ? 'unsubscribed from' : 'subscribed to';
        showSnackbar(`Successfully ${action} notifications`, 'success');
      } catch (error) {
        showSnackbar('Failed to update notification settings', 'error');
      }
    }, [onToggleNotifications, isNotificationSubscribed, showSnackbar]);

    const handleCalendarExport = useCallback(
      (format: 'ics' | 'google' | 'outlook') => {
        setCalendarMenuAnchor(null);

        try {
          switch (format) {
            case 'ics':
              downloadICSFile(meeting, group?.name);
              showSnackbar('Calendar file downloaded', 'success');
              break;
            case 'google':
              window.open(
                generateGoogleCalendarURL(meeting),
                '_blank',
                'noopener,noreferrer',
              );
              break;
            case 'outlook':
              window.open(generateOutlookURL(meeting), '_blank', 'noopener,noreferrer');
              break;
          }
        } catch (error) {
          showSnackbar('Failed to export to calendar', 'error');
        }
      },
      [meeting, group?.name, showSnackbar],
    );

    const handleShare = useCallback(
      (method: 'clipboard' | 'native') => {
        setShareMenuAnchor(null);

        const shareData = {
          title: meeting.title,
          text: `Join me at "${meeting.title}" ${group?.name ? `hosted by ${group.name}` : ''}`,
          url: window.location.href,
        };

        if (method === 'native' && navigator.share) {
          navigator.share(shareData).catch(() => {
            showSnackbar('Sharing cancelled', 'info');
          });
        } else {
          // Fallback to clipboard
          const shareText = `${shareData.title}\\n\\n${shareData.text}\\n\\n${shareData.url}`;
          navigator.clipboard
            .writeText(shareText)
            .then(() => showSnackbar('Link copied to clipboard', 'success'))
            .catch(() => showSnackbar('Failed to copy link', 'error'));
        }
      },
      [meeting.title, group?.name, showSnackbar],
    );

    if (!user) {
      return (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              Sign in to RSVP and interact with this event
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <>
        <Card>
          <CardContent>
            <Stack spacing={3}>
              {/* RSVP Section */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Your Response
                </Typography>

                {/* Capacity Warning */}
                {isAtCapacity && rsvpStatus !== 'attending' && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This event is at capacity. You can join the waitlist by RSVPing as
                    attending.
                  </Alert>
                )}

                <Stack direction="row" spacing={2}>
                  <Button
                    variant={rsvpStatus === 'attending' ? 'contained' : 'outlined'}
                    color={
                      isAtCapacity && rsvpStatus !== 'attending' ? 'warning' : 'success'
                    }
                    onClick={() => handleRSVP('attending')}
                    disabled={rsvpLoading || !canAttend}
                    size="large"
                  >
                    {isAtCapacity && rsvpStatus !== 'attending'
                      ? 'Join Waitlist'
                      : 'Attending'}
                  </Button>
                  <Button
                    variant={rsvpStatus === 'not_attending' ? 'contained' : 'outlined'}
                    color="error"
                    onClick={() => handleRSVP('not_attending')}
                    disabled={rsvpLoading || !canRSVP}
                    size="large"
                  >
                    Not Attending
                  </Button>
                </Stack>

                {/* RSVP Status Info with Approval Status */}
                {rsvpStatus && rsvp && (
                  <Box sx={{ mt: 2 }}>
                    {/* Base RSVP Status */}
                    <Typography variant="body2" color="text.secondary">
                      You've RSVP'd as {rsvpStatus.replace('_', ' ')}.
                    </Typography>

                    {/* Approval Status - only show for attending RSVPs that require approval */}
                    {meeting.requiresApproval && rsvpStatus === 'attending' && (
                      <>
                        {rsvp.approvalStatus === 'pending' && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            Your RSVP is pending approval from the organizer.
                          </Alert>
                        )}
                        {rsvp.approvalStatus === 'approved' && (
                          <Alert severity="success" sx={{ mt: 1 }}>
                            Your RSVP has been approved!
                          </Alert>
                        )}
                        {rsvp.approvalStatus === 'rejected' && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            Your RSVP was not approved.
                            {rsvp.rejectionReason && (
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                Reason: {rsvp.rejectionReason}
                              </Typography>
                            )}
                          </Alert>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Box>

              <Divider />

              {/* Notifications */}
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isNotificationSubscribed}
                      onChange={handleToggleNotifications}
                      disabled={
                        notificationLoading ||
                        !isPushSupported ||
                        notificationPermission !== 'granted'
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Event Notifications</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Get notified of updates and reminders
                      </Typography>
                    </Box>
                  }
                />

                {!isPushSupported && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    Push notifications not supported in this browser
                  </Typography>
                )}

                {isPushSupported && notificationPermission !== 'granted' && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    Enable notifications in your browser settings
                  </Typography>
                )}
              </Box>

              <Divider />

              {/* Smart Join/Location Buttons based on Event Type */}
              <Stack spacing={1}>
                {(meeting.eventType === 'online' || meeting.eventType === 'hybrid') &&
                  meeting.onlineUrl && (
                    <Button
                      variant="contained"
                      color="primary"
                      href={meeting.onlineUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<VideocamIcon />}
                      fullWidth
                    >
                      {meeting.eventType === 'hybrid' ? 'Join Online' : 'Join Video Call'}
                    </Button>
                  )}

                {(meeting.eventType === 'in-person' || meeting.eventType === 'hybrid') &&
                  meeting.location &&
                  meeting.location.mapUrl && (
                    <Button
                      variant={meeting.eventType === 'hybrid' ? 'outlined' : 'contained'}
                      color="primary"
                      href={meeting.location.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<PlaceIcon />}
                      fullWidth
                    >
                      {meeting.eventType === 'hybrid'
                        ? 'Get Directions'
                        : 'View Location'}
                    </Button>
                  )}
              </Stack>

              <Divider />

              {/* Action Buttons */}
              <Stack direction="row" spacing={1}>
                {/* Add to Calendar */}
                <Button
                  variant="outlined"
                  startIcon={<CalendarTodayIcon />}
                  onClick={event => setCalendarMenuAnchor(event.currentTarget)}
                  fullWidth
                >
                  Add to Calendar
                </Button>

                {/* Share */}
                <Button
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={event => setShareMenuAnchor(event.currentTarget)}
                  fullWidth
                >
                  Share Event
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Calendar Export Menu */}
        <Menu
          anchorEl={calendarMenuAnchor}
          open={Boolean(calendarMenuAnchor)}
          onClose={() => setCalendarMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MenuItem onClick={() => handleCalendarExport('ics')}>
            <EventIcon sx={{ mr: 2 }} />
            Download .ics file
          </MenuItem>
          <MenuItem onClick={() => handleCalendarExport('google')}>
            <GoogleIcon sx={{ mr: 2 }} />
            Google Calendar
          </MenuItem>
          <MenuItem onClick={() => handleCalendarExport('outlook')}>
            <CalendarTodayIcon sx={{ mr: 2 }} />
            Outlook Calendar
          </MenuItem>
        </Menu>

        {/* Share Menu */}
        <Menu
          anchorEl={shareMenuAnchor}
          open={Boolean(shareMenuAnchor)}
          onClose={() => setShareMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {'share' in navigator && (
            <MenuItem onClick={() => handleShare('native')}>
              <ShareIcon sx={{ mr: 2 }} />
              Share via...
            </MenuItem>
          )}
          <MenuItem onClick={() => handleShare('clipboard')}>
            <ShareIcon sx={{ mr: 2 }} />
            Copy link
          </MenuItem>
        </Menu>

        {/* Feedback Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  },
);

EventActions.displayName = 'EventActions';

export default EventActions;
